'use client'

import { Alert, Box, Button, Chip, MenuItem, Stack, TextField } from '@mui/material'
import { enUS } from '@mui/material/locale'
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles'
import { enUS as dataGridEnUS } from '@mui/x-data-grid-premium/locales'
import { enUS as pickersEnUS } from '@mui/x-date-pickers-pro/locales'
import { useState } from 'react'

import themeOptions from '@core/theme/ThemeOptions'

import LoadingButton from '~/components/LoadingButton'
import { Overlay, createOverlay, useOverlay } from '~/components/Overlay'
import { switchBackend } from '~/configs/backendSwitch'
import { BACKEND_KEYS, type BackendKey, activeBackend, allowsRuntimeBackendSwitch } from '~/configs/env'

// Built from the initial, org-unaware settings so the switcher renders with a
// consistent look regardless of the signed-in org (or whether anyone is signed
// in at all). It lives in the React tree above ThemeComponent because that
// provider is auth-coupled.
const baseTheme = responsiveFontSizes(
  createTheme(
    themeOptions({ mode: 'light', themeId: 'light', direction: 'ltr', navCollapsed: false }, 'light'),
    enUS,
    dataGridEnUS,
    pickersEnUS
  )
)

const BACKEND_INFO: Record<BackendKey, { label: string; color: 'success' | 'error' | 'info' }> = {
  gamma: { label: 'gamma', color: 'success' },
  prod: { label: 'prod US', color: 'error' },
  prod_eu: { label: 'prod EU', color: 'info' }
}

export default function BackendSwitcher() {
  const overlay = useOverlay()

  if (!allowsRuntimeBackendSwitch) return null

  return (
    <ThemeProvider theme={baseTheme}>
      {/* AssistantBar publishes its measured height on <html> while mounted, so
          the chip rides 16px above it. The 0px fallback leaves just that 16px
          inset from the viewport bottom whenever the bar is absent. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 'calc(var(--synop-assistant-bar-height, 0px) + 16px)',
          right: 16,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          transition: 'bottom .25s ease-in-out'
        }}
      >
        <Chip
          size="small"
          label={`Backend: ${BACKEND_INFO[activeBackend].label}`}
          color={BACKEND_INFO[activeBackend].color}
          onClick={overlay.openOverlay}
          clickable
          sx={{ boxShadow: 3, fontWeight: 600 }}
          aria-label={`Active backend: ${BACKEND_INFO[activeBackend].label}. Click to change.`}
        />
      </Box>

      <BackendSwitcherOverlay {...overlay} />
    </ThemeProvider>
  )
}

const BackendSwitcherOverlay = createOverlay(({ closeOverlay }) => {
  const [selected, setSelected] = useState<BackendKey>(activeBackend)
  const [isSwitching, setIsSwitching] = useState(false)

  const handleApply = () => {
    if (selected === activeBackend) {
      closeOverlay()
      return
    }
    setIsSwitching(true)
    // On the happy path `switchBackend` returns `true` and the page reloads
    // moments later, tearing down this component — the spinner stays visible
    // until then. On an early-return (e.g. localStorage unavailable) it
    // returns `false` and we reset state so the user can retry.
    if (!switchBackend(selected)) setIsSwitching(false)
  }

  return (
    <Overlay fullWidth maxWidth="xs">
      <Overlay.Header title="Switch backend" />
      <Overlay.Content>
        <Stack spacing={2}>
          <TextField
            select
            label="Backend"
            value={selected}
            onChange={(e) => setSelected(e.target.value as BackendKey)}
            disabled={isSwitching}
            fullWidth
          >
            {BACKEND_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {BACKEND_INFO[key].label}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="warning">
            Switching backends clears local and session storage and reloads the page. You will be signed out and need
            to sign in again against the selected backend.
          </Alert>
        </Stack>
      </Overlay.Content>
      <Overlay.Actions>
        <Button onClick={closeOverlay} disabled={isSwitching}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleApply}
          variant="contained"
          loading={isSwitching}
          disabled={selected === activeBackend}
        >
          Switch
        </LoadingButton>
      </Overlay.Actions>
    </Overlay>
  )
})
